import { dehydrate, hydrate } from '@tanstack/react-query';

const DB_NAME = 'calendar-app-cache';
const STORE_NAME = 'query-cache';
const CACHE_ID = 'react-query';
const CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000;
const PERSIST_DEBOUNCE_MS = 1000;
const PERSISTED_QUERY_ROOTS = new Set(['events', 'tasks', 'calendars']);

function isBrowser() {
  return typeof window !== 'undefined' && typeof window.indexedDB !== 'undefined';
}

function openCacheDb() {
  if (!isBrowser()) return Promise.resolve(null);

  return new Promise((resolve, reject) => {
    const request = window.indexedDB.open(DB_NAME, 1);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
    request.onblocked = () => resolve(null);
  });
}

async function withStore(mode, callback) {
  const db = await openCacheDb();
  if (!db) return null;

  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, mode);
    const store = transaction.objectStore(STORE_NAME);
    const request = callback(store);

    request.onsuccess = () => resolve(request.result ?? null);
    request.onerror = () => reject(request.error);
    transaction.oncomplete = () => db.close();
    transaction.onerror = () => {
      db.close();
      reject(transaction.error);
    };
    transaction.onabort = () => {
      db.close();
      reject(transaction.error);
    };
  });
}

function isPersistedQuery(query) {
  const rootKey = Array.isArray(query.queryKey) ? query.queryKey[0] : null;
  if (!PERSISTED_QUERY_ROOTS.has(rootKey)) return false;
  if (query.state.status !== 'success') return false;

  const data = query.state.data;
  if (Array.isArray(data) && data.some(item => item?._optimistic)) {
    return false;
  }

  return data !== undefined;
}

async function readPersistedClient() {
  const record = await withStore('readonly', store => store.get(CACHE_ID));
  if (!record?.clientState || !record.savedAt) return null;

  if (Date.now() - record.savedAt > CACHE_MAX_AGE_MS) {
    await clearPersistedQueryCache();
    return null;
  }

  return record.clientState;
}

async function writePersistedClient(queryClient) {
  const clientState = dehydrate(queryClient, {
    shouldDehydrateQuery: isPersistedQuery,
  });

  if (!clientState.queries.length) {
    await clearPersistedQueryCache();
    return;
  }

  await withStore('readwrite', store => store.put({
    id: CACHE_ID,
    savedAt: Date.now(),
    clientState,
  }));
}

export async function restoreQueryCache(queryClient) {
  try {
    const clientState = await readPersistedClient();
    if (clientState) hydrate(queryClient, clientState);
  } catch (error) {
    console.warn('Failed to restore cached app data:', error);
  }
}

export function startQueryCachePersistence(queryClient) {
  if (!isBrowser()) return () => {};

  let timeoutId = null;

  const persistSoon = () => {
    window.clearTimeout(timeoutId);
    timeoutId = window.setTimeout(() => {
      writePersistedClient(queryClient).catch(error => {
        console.warn('Failed to persist cached app data:', error);
      });
    }, PERSIST_DEBOUNCE_MS);
  };

  const flush = () => {
    window.clearTimeout(timeoutId);
    timeoutId = null;
    return writePersistedClient(queryClient).catch(error => {
      console.warn('Failed to persist cached app data:', error);
    });
  };

  const unsubscribe = queryClient.getQueryCache().subscribe(persistSoon);

  const handleVisibilityChange = () => {
    if (document.visibilityState === 'hidden') flush();
  };

  document.addEventListener('visibilitychange', handleVisibilityChange);
  window.addEventListener('pagehide', flush);

  return () => {
    window.clearTimeout(timeoutId);
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    window.removeEventListener('pagehide', flush);
    unsubscribe();
  };
}

export async function clearPersistedQueryCache() {
  try {
    await withStore('readwrite', store => store.delete(CACHE_ID));
  } catch (error) {
    console.warn('Failed to clear cached app data:', error);
  }
}
